import { createContext, useContext, useState } from 'react';

const CartContext = createContext(null);

// Calcula soma dos adicionais de um item (já multiplicado pela qty do item)
function precoAdicionais(item) {
  const ad = item.adicionais || [];
  const somaAd = ad.reduce((s, a) => s + (Number(a.preco) * (a.quantidade || 1)), 0);
  return somaAd * item.qty;
}

function precoItem(item) {
  return (Number(item.preco) * item.qty) + precoAdicionais(item);
}

export function CartProvider({ children }) {
  const [cart, setCart] = useState([]);

  const addItem = (produto) => setCart(prev => {
    // procura linha do mesmo produto SEM personalização
    const idx = prev.findIndex(i =>
      i.id === produto.id &&
      (!i.adicionais || i.adicionais.length === 0) &&
      (!i.remover || i.remover.length === 0) &&
      !(i.observacao || '').trim()
    );
    if (idx !== -1) {
      return prev.map((i, k) => k === idx ? { ...i, qty: i.qty + 1 } : i);
    }
    return [...prev, { ...produto, qty: 1, adicionais: [], remover: [], observacao: '', _uid: 'u' + Date.now() + Math.random().toString(36).slice(2,6) }];
  });

  const removeItem = (uid) => setCart(prev => prev.filter(i => i._uid !== uid));

  const changeQty = (uid, delta) => setCart(prev =>
    prev.map(i => i._uid === uid ? { ...i, qty: i.qty + delta } : i).filter(i => i.qty > 0)
  );

  const updateItemOpts = (uid, opts) => setCart(prev =>
    prev.map(i => i._uid === uid ? { ...i, ...opts } : i)
  );

  // Separa X unidades de um item pra receber combo/opts diferentes
  // Retorna o ID da nova linha criada (uuid interno)
  const splitItem = (uid, qtdSeparar, novosOpts) => {
    let novoUid = null;
    setCart(prev => {
      const idx = prev.findIndex(i => i._uid === uid);
      if (idx === -1) return prev;
      const item = prev[idx];
      if (qtdSeparar >= item.qty) {
        // aplica em todos - só atualiza o item atual
        return prev.map(i => i._uid === uid ? { ...i, ...novosOpts } : i);
      }
      novoUid = 'u' + Date.now() + Math.random().toString(36).slice(2, 6);
      const original = { ...item, qty: item.qty - qtdSeparar };
      const novo = { ...item, ...novosOpts, qty: qtdSeparar, _uid: novoUid };
      const copia = [...prev];
      copia.splice(idx + 1, 0, novo);
      copia[idx] = original;
      return copia;
    });
    // retorna promessa pro caller saber qual _uid criado
    return new Promise(r => setTimeout(() => r(novoUid), 0));
  };

  // Mescla linhas iguais (mesmo produto + mesmos adicionais + mesmo remover + mesma obs)
  const mergeIguais = () => setCart(prev => {
    const chave = (i) => JSON.stringify({
      id: i.id,
      ad: (i.adicionais || []).map(a => ({ id: a.id, q: a.quantidade || 1 })).sort((a,b) => a.id - b.id),
      rm: (i.remover || []).slice().sort(),
      obs: (i.observacao || '').trim()
    });
    const map = new Map();
    for (const i of prev) {
      const k = chave(i);
      if (map.has(k)) {
        const ex = map.get(k);
        ex.qty += i.qty;
      } else {
        map.set(k, { ...i });
      }
    }
    return Array.from(map.values());
  });

  const clearCart = () => setCart([]);

  const totalItems = cart.reduce((a, i) => a + i.qty, 0);
  const subtotal   = cart.reduce((a, i) => a + precoItem(i), 0);
  const total      = subtotal + 3.00;

  return (
    <CartContext.Provider value={{
      cart, addItem, removeItem, changeQty, updateItemOpts, splitItem, mergeIguais, clearCart,
      totalItems, subtotal, total, precoItem, precoAdicionais
    }}>
      {children}
    </CartContext.Provider>
  );
}

export const useCart = () => useContext(CartContext);
